import { queryOptions, useQueryClient, type QueryOptions, type UseMutationOptions } from '@tanstack/vue-query';
import type {
    AgentActionStatusResult, AgentDecisionBody, AgentFlagBody, AgentHistoryQuery, AgentModelsResult, AgentPolicyBody, AgentPolicyResult, SessionHistoryResult, SessionReplay
} from '@sysarb/sysarb-types-v2/services/v1/agent-host';

import { getApiHost } from '@/appSettings';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { RestManager } from '@/newApi/services/rest/RestManager';

export type FlagReason = AgentFlagBody['reason'];

export type DecideActionInput = AgentDecisionBody & { actionId: string };

type AgentHostOptions = {
    queries: {
        history: (feature: AgentHistoryQuery['feature']) => QueryOptions<SessionHistoryResult, Error>;
        session: (sessionId: string) => QueryOptions<SessionReplay, Error>;
        models: () => QueryOptions<AgentModelsResult, Error>;
    };
    mutations: {
        flagAnswer: () => UseMutationOptions<boolean, Error, AgentFlagBody>;
        decideAction: () => UseMutationOptions<AgentActionStatusResult, Error, DecideActionInput>;
        setAutoApprove: () => UseMutationOptions<AgentPolicyResult, Error, AgentPolicyBody>;
        retryAction: () => UseMutationOptions<AgentActionStatusResult, Error, string>;
        hideSession: (feature: AgentHistoryQuery['feature']) => UseMutationOptions<void, Error, string>;
    };
};

export const agentHostUrl = (instance: string | null): string => `https://${getApiHost()}/agent-host-service/v1/${instance ?? ''}`;

const agentService = (instance: string | null): RestManager => new RestManager(agentHostUrl(instance));

export const agentHistoryKey = (instance: string | null, feature: AgentHistoryQuery['feature']): unknown[] => ['agent-history', instance, feature];

export const agentHost: AgentHostOptions = {
    queries: {
        history: (feature: AgentHistoryQuery['feature']): QueryOptions<SessionHistoryResult, Error> => {
            const { instance } = useLoginStore();

            return queryOptions<SessionHistoryResult, Error>({
                queryKey: agentHistoryKey(instance, feature),
                queryFn: async (): Promise<SessionHistoryResult> => {
                    const query: AgentHistoryQuery = { feature };

                    return agentService(instance).get<SessionHistoryResult>(`agent/history?${new URLSearchParams({ ...query }).toString()}`);
                },
            });
        },
        session: (sessionId: string): QueryOptions<SessionReplay, Error> => {
            const { instance } = useLoginStore();

            return queryOptions<SessionReplay, Error>({
                queryKey: ['agent-session', instance, sessionId],
                queryFn: async (): Promise<SessionReplay> => agentService(instance).get<SessionReplay>(`agent/history/${sessionId}`),
            });
        },
        // Fixed for the life of a release -  so it is read once
        models: (): QueryOptions<AgentModelsResult, Error> => {
            const { instance } = useLoginStore();

            return queryOptions<AgentModelsResult, Error>({
                queryKey: ['agent-models', instance],
                queryFn: async (): Promise<AgentModelsResult> => agentService(instance).get<AgentModelsResult>('agent/models'),
                staleTime: Infinity,
            });
        },
    },
    mutations: {
        flagAnswer: (): UseMutationOptions<boolean, Error, AgentFlagBody> => {
            const { instance } = useLoginStore();

            return {
                mutationFn: async ({ runId, reason, note }: AgentFlagBody): Promise<boolean> => {
                    const body: AgentFlagBody = { runId, reason, ...(note === undefined || note === '' ? {} : { note }) };
                    await agentService(instance).post<void>(body, 'agent/flag');

                    return true;
                },
            };
        },
        decideAction: (): UseMutationOptions<AgentActionStatusResult, Error, DecideActionInput> => {
            const { instance } = useLoginStore();

            return {
                mutationFn: async ({ actionId, decision, reason }: DecideActionInput): Promise<AgentActionStatusResult> => {
                    const body: AgentDecisionBody = { decision, ...(reason === undefined || reason === '' ? {} : { reason }) };

                    return agentService(instance).post<AgentActionStatusResult>(body, `agent/actions/${actionId}/decision`);
                },
            };
        },
        setAutoApprove: (): UseMutationOptions<AgentPolicyResult, Error, AgentPolicyBody> => {
            const { instance } = useLoginStore();

            return {
                mutationFn: async (body: AgentPolicyBody): Promise<AgentPolicyResult> => agentService(instance).post<AgentPolicyResult>(body, 'agent/policies'),
            };
        },
        // The action id is the idempotency key, so a retry cannot become a second effect.
        retryAction: (): UseMutationOptions<AgentActionStatusResult, Error, string> => {
            const { instance } = useLoginStore();

            return {
                mutationFn: async (actionId: string): Promise<AgentActionStatusResult> =>
                    agentService(instance).post<AgentActionStatusResult>(undefined, `agent/actions/${actionId}/retry`),
            };
        },
        hideSession: (feature: AgentHistoryQuery['feature']): UseMutationOptions<void, Error, string> => {
            const queryClient = useQueryClient();
            const { instance } = useLoginStore();

            return {
                mutationFn: async (sessionId: string): Promise<void> => {
                    await agentService(instance).post<unknown>(undefined, `agent/history/${sessionId}/hide`);
                },
                onSettled: async (): Promise<void> => {
                    await queryClient.invalidateQueries({ queryKey: agentHistoryKey(instance, feature) });
                },
            };
        },
    },
};
